import React, { useState } from 'react'
import { Box, Button, Chip, Grid, Paper, Typography } from '@material-ui/core'
import {
  MinusCircle as MinusCircleIcon,
  Download as DownloadIcon,
} from 'react-feather'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import PerfectScrollbar from 'react-perfect-scrollbar'

import { Container, ContentHeader, LoadingFeedback, Page } from 'components'
import { ConsentLogBox, ConsentRevoactionDialog } from './components'
import { ConsentReportPdf } from 'views/Reports/components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'

import constants from 'constants/index'
import * as service from 'service'
import useStyles from './styles'
import theme from 'theme'

const ConsentReceiptDetails = ({ match }) => {
  const classes = useStyles()

  const [openDialog, setOpenDialog] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)

  const {
    AUTHORIZED_CONSENT,
    DPONET_PLATFORM,
    LEGAL_BASE_LABEL_CONSENT,
    STATUS_LABEL_RECEIPT,
  } = constants.consent

  const { consentFormId, consentReceiptId } = match.params

  const { response, isLoading } = useFetch(service.dponet.consent.getReceipt, {
    consentFormId,
    id: consentReceiptId,
  })

  const consentFormAnswer = response?.data?.consentFormAnswer
  const answers =
    consentFormAnswer?.answers && JSON.parse(consentFormAnswer?.answers)

  const handleActionDialog = () => {
    setOpenDialog(!openDialog)
  }

  const generatePdfReceipt = async () => {
    setExportingPdf(true)
  }

  return (
    <Page title="Detalhes do recibo">
      <LoadingFeedback open={isLoading} />
      <Container maxWidth={false}>
        <ContentHeader title="Detalhes do recibo">
          <Button
            startIcon={<DownloadIcon />}
            type="submit"
            variant="outlined"
            onClick={generatePdfReceipt}
            className={classes.button}
          >
            Baixar Recibo
          </Button>
          {consentFormAnswer?.platform === DPONET_PLATFORM &&
            consentFormAnswer?.status === AUTHORIZED_CONSENT && (
              <Box marginX={1}>
                <Button
                  startIcon={<MinusCircleIcon />}
                  className={classes.buttonError}
                  onClick={handleActionDialog}
                >
                  Revogar Consentimento
                </Button>
              </Box>
            )}
        </ContentHeader>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Paper>
              <Box width="100%" p={3} fontWeight={500}>
                <Typography variant="h4" color="primary">
                  Detalhes do Recibo
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item md={8}>
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(4)}
            >
              <Box>
                <Paper className={classes.receiptTitleBox}>
                  <Typography variant="subtitle1">
                    {consentFormAnswer?.consentForm?.title}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {
                      LEGAL_BASE_LABEL_CONSENT[
                        consentFormAnswer?.consentForm?.legalFrameworkId
                      ]
                    }
                  </Typography>
                </Paper>
                <Paper className={classes.contentPaper}>
                  <PerfectScrollbar
                    options={{
                      wheelPropagation: false,
                      useBothWheelAxes: true,
                    }}
                    className={classes.scrollbar}
                  >
                    <Box
                      maxHeight={300}
                      fontFamily="Roboto"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          consentFormAnswer?.consentForm?.content,
                        ),
                      }}
                    />
                  </PerfectScrollbar>
                </Paper>
              </Box>
              <ConsentLogBox consentFormAnswer={consentFormAnswer} />
            </Box>
          </Grid>
          <Grid item md={4}>
            <Paper>
              <Box
                className={clsx(classes.formTitleBox, classes.detailsItemBox)}
                borderBottom={`1px solid ${theme.palette.grey[300]}`}
              >
                <Typography variant="subtitle2">
                  Título do Formulário
                </Typography>
                <Typography color="textSecondary">
                  {consentFormAnswer?.consentForm?.title}
                </Typography>
              </Box>
              <Box
                className={classes.detailsItemBox}
                borderBottom={`1px solid ${theme.palette.grey[300]}`}
              >
                <Typography variant="subtitle2">E-mail</Typography>
                <Typography color="textSecondary">
                  {consentFormAnswer?.userEmail || 'Não informado'}
                </Typography>
              </Box>
              <Box
                className={classes.detailsItemBox}
                borderBottom={`1px solid ${theme.palette.grey[300]}`}
              >
                <Typography variant="subtitle2">Criado em</Typography>
                <Typography color="textSecondary">
                  {helpers.formatters.date(consentFormAnswer?.createdAt)}
                </Typography>
              </Box>
              <Box
                className={classes.detailsItemBox}
                borderBottom={`1px solid ${theme.palette.grey[300]}`}
              >
                <Typography variant="subtitle2">Última atualização</Typography>
                <Typography color="textSecondary">
                  {helpers.formatters.date(consentFormAnswer?.updatedAt)}
                </Typography>
              </Box>
              {answers &&
                Object.entries(answers).map(
                  ([key, value]) =>
                    !!value && (
                      <Box
                        key={key}
                        className={classes.detailsItemBox}
                        borderBottom={`1px solid ${theme.palette.grey[300]}`}
                      >
                        <Typography variant="subtitle2">{key}</Typography>
                        <Typography color="textSecondary">{value}</Typography>
                      </Box>
                    ),
                )}
              <Box className={classes.detailsItemBox}>
                <Typography variant="subtitle2">Status</Typography>
                <Box>
                  <Chip
                    className={
                      consentFormAnswer?.status === AUTHORIZED_CONSENT
                        ? classes.statusLabelAuthorized
                        : classes.statusLabelRevoked
                    }
                    label={
                      STATUS_LABEL_RECEIPT[
                        consentFormAnswer?.status
                      ]?.toUpperCase() || 'Não definido'
                    }
                  />
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <ConsentRevoactionDialog
        open={openDialog}
        onClose={handleActionDialog}
        consent={consentFormAnswer}
      />
      <ConsentReportPdf
        exportingPdf={exportingPdf}
        receipt={consentFormAnswer}
        setExportingPdf={setExportingPdf}
        reportSelected={constants.reports.TYPES_CONSENTS.RECEIPT_CONSENT}
      />
    </Page>
  )
}

export default ConsentReceiptDetails
